// src/context/UserContext.js

import { createContext, useContext, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useAuth } from "./AuthContext";
import { apiRequest } from "../config/api";

const UserContext = createContext(null);

export function UserProvider({ children }) {
  const { user, loading: authLoading, getToken } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const lastDataHash = useRef(null);

  // Función para calcular hash de los datos (para detectar cambios)
  const calculateDataHash = (data) => {
    if (!data) return null;
    return JSON.stringify(data);
  };

  // Función para obtener datos del usuario
  const fetchUserData = async () => {
    // Esperar a que la autenticación esté lista
    if (authLoading) {
      return;
    }

    // Si no hay usuario autenticado, limpiar datos
    if (!user) {
      setUserData(null);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await apiRequest('/datos-usuario', {
        method: 'GET',
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('No autorizado. Por favor, inicia sesión nuevamente.');
        }
        throw new Error(`Error al obtener datos del usuario: ${response.status}`);
      }

      const data = await response.json();
      
      // Calcular hash de los nuevos datos
      const newDataHash = calculateDataHash(data);
      
      // Solo actualizar si los datos han cambiado
      if (newDataHash !== lastDataHash.current) {
        setUserData(data);
        lastDataHash.current = newDataHash;
        
        // Guardar datos útiles en localStorage para compatibilidad
        if (data.id) {
          localStorage.setItem('userId', data.id);
        }
        if (data.empresaId) {
          localStorage.setItem('empresaId', data.empresaId);
        }
        if (data.comision !== undefined) {
          localStorage.setItem('comision', data.comision);
        }
      }
    } catch (err) {
      console.error('Error al obtener datos del usuario:', err);
      setError(err.message);
      setUserData(null);
    } finally {
      setLoading(false);
    }
  };

  // Efecto para cargar datos cuando el usuario se autentica
  useEffect(() => {
    if (!authLoading) {
      fetchUserData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  // Función para refrescar manualmente los datos
  const refreshUserData = async () => {
    lastDataHash.current = null; // Resetear hash para forzar actualización
    await fetchUserData();
  };

  const value = {
    userData,
    loading,
    error,
    refreshUserData,
    getToken, // Exponer getToken para compatibilidad
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

UserProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser debe ser usado dentro de un UserProvider");
  }
  return context;
}

